<template>
    <Upload
        :action="uploadAction"
        :data="formData"
        :show-upload-list="false"
        :on-success="onTemplateUploadSuccess"
        :on-error="onTemplateUploadError"
        :on-progress="showUploadPercentage"
        :multiple="false"
    >
        <Button ref="remote" class="hidden" @click="updatePostData" />
    </Upload>

    <hox-modal v-if="showUpdateTemplatePrompt" @close="updateTemplatePromptCancel">
        <template #header>Are you sure?</template>

        <div class="template-upload-modal">
            <div v-if="templateUsedInCampaigns?.length">
                <p class="small-margin">This template is used in the following campaigns:</p>

                <ul>
                    <li v-for="campaign in templateUsedInCampaigns" :key="campaign._id">
                        {{ campaign.name }}
                    </li>
                </ul>
            </div>

            <p class="small-margin">{{ updateTemplateModalAdsText }}</p>

            <hox-alert v-if="hasGroupConflicts" margin-bottom="none" type="danger" size="small">
                <template #title>The template you are uploading contains different groups.</template>
                <template #content>
                    <p class="template-upload-modal__alert-text">
                        Any deliverables which are affected by these changes will no longer be accessible. You will lose
                        access to any comments, annotations and status set on these deliverables, and you should rebuild
                        any feeds that have been published.
                    </p>
                    <template v-if="editableGroupConflicts.added.length">
                        <span class="template-upload-modal__label">This template contains these new groups:</span>
                        <ul>
                            <li v-for="added in editableGroupConflicts.added" :key="added" class="capitalize-text">
                                {{ added }}
                            </li>
                        </ul>
                    </template>
                    <div v-if="editableGroupConflicts.removed.length" class="template-upload-modal__removed">
                        <span class="template-upload-modal__label"> This template has removed following groups: </span>
                        <ul>
                            <li
                                v-for="removed in editableGroupConflicts.removed"
                                :key="removed"
                                class="capitalize-text"
                            >
                                {{ removed }}
                            </li>
                        </ul>
                    </div>
                </template>
            </hox-alert>

            <hox-alert
                v-if="templateFamily.length && templateFamilyMaster"
                margin-bottom="none"
                type="danger"
                size="small"
            >
                <template #title>Template family detected</template>
                <template #content>
                    <p v-if="familyUpdateInProgress" class="template-upload-modal__alert-text">
                        Updating family members:
                    </p>
                    <p v-else class="template-upload-modal__alert-text">
                        This template belongs to the "{{ templateFamilyMaster.name }}" family. The Master size and all
                        additional sizes in this family will be updated.
                    </p>

                    <ul v-if="familyUpdateInProgress">
                        <div v-for="tplId in familyMembersToUpdate" :key="tplId">
                            <FamilyUpdateProgressRow
                                :is-master="!templateFamilyById[tplId].parentId"
                                :is-loading="familyMembersUpdated[tplId] === undefined"
                                :has-failed="familyMembersUpdated[tplId] === false"
                            >
                                {{ templateFamilyById[tplId].width }}x{{ templateFamilyById[tplId].height }}
                            </FamilyUpdateProgressRow>
                        </div>
                    </ul>
                    <ul v-else>
                        <li>{{ templateFamilyMaster.width }}x{{ templateFamilyMaster.height }} (Master)</li>
                        <li v-for="tpl in templateFamilyResizes" :key="tpl._id">{{ tpl.width }}x{{ tpl.height }}</li>
                    </ul>
                </template>
            </hox-alert>
        </div>

        <template #footer>
            <Button
                v-if="familyUpdateInProgress"
                class="template-upload-modal__button"
                @click="showUpdateTemplatePrompt = false"
            >
                Close
            </Button>
            <template v-else>
                <Button class="template-upload-modal__button" @click="updateTemplatePromptCancel">Cancel</Button>
                <Button class="template-upload-modal__button" type="primary" @click="updateTemplatePromptOk">
                    <template v-if="familyMembersToUpdate.length > 1">Update family</template>
                    <template v-else>Go ahead</template>
                </Button>
            </template>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import type { Campaign, MasterTemplate } from "@/types/types";

import clientMasterTemplatesUploadConfigQuery from "@/apollo/queries/v2/ClientMasterTemplatesUploadConfig.gql";
import bus from "@/bus";
import FamilyUpdateProgressRow from "@/components/Campaign/MasterTemplates/FamilyUpdateProgressRow.vue";
import MasterTemplates, * as MasterTemplatesTypes from "@/services/MasterTemplates";
import { MasterTemplateLibraryAction } from "@/store/modules/masterTemplateLibrary";
import { uploadConfigExpired } from "@/utils";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import { GA4_EVENT_TYPES, GA4_FEATURE_CATEGORIES, GA4_FEATURE_CONTEXTS } from "@/enums/ga4Events";

export default defineComponent({
    name: "MasterTemplateReplaceButton",
    components: { FamilyUpdateProgressRow },
    inject: {
        refetchMasterTemplates: {
            from: "refetchMasterTemplates",
            default: null as (() => void) | null
        }
    },
    props: {
        masterTemplateId: {
            type: String,
            default: null
        }
    },

    emits: ["updateStarted", "updated", "familyUpdated"],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            isLoading: false,
            editableGroupConflicts: { added: [], removed: [] } as { added: string[]; removed: string[] },
            familyMembersToUpdate: [] as string[],
            familyMembersUpdated: {} as Record<string, boolean>,
            familyUpdateInProgress: false,
            templateFamily: [] as MasterTemplate[],
            templateFamilyById: {} as Record<string, MasterTemplate>,
            templateUsedPromptResolve: null as null | ((result: boolean) => void),
            formData: {},
            showUpdateTemplatePrompt: false,
            templateUsedInCampaigns: [] as Campaign[],
            templateUsedProducedAds: {} as { published: number; unpublished: number },
            masterTemplateService: null as null | MasterTemplates
        };
    },

    computed: {
        canManageMasterTemplates() {
            return this.$auth.userCan(
                this.$auth.Actions.CanManageMasterTemplates,
                { clientId: this.clientId },
                false
            ) as boolean;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        hasGroupConflicts() {
            return this.editableGroupConflicts.added.length || this.editableGroupConflicts.removed.length;
        },

        templateFamilyMaster() {
            return this.templateFamily.find(({ parentId }) => parentId === null);
        },

        templateFamilyResizes() {
            return this.templateFamily.filter(({ parentId }) => parentId);
        },

        updateTemplateModalAdsText() {
            const { published = 0, unpublished = 0 } = this.templateUsedProducedAds ?? {};

            const xAds = published + unpublished === 1 ? " ad" : " ads";
            const publishedText = published > 0 ? `${published} published` : "";
            const unpublishedText = unpublished > 0 ? `${unpublished} unpublished` : "";
            const adsText = [publishedText, unpublishedText].filter(text => text).join(" and ");

            return `This action will affect ${adsText} ${xAds}.`;
        },

        uploadAction() {
            return this.uploadConfig?.action || "";
        },

        uploadConfig() {
            return this.uploadConfigObject?.config;
        },

        uploadConfigObject() {
            return this.$store.state.masterTemplateLibrary.uploadConfig;
        },

        templateType() {
            const masterTemplates = this.$store.state.masterTemplateLibrary.masterTemplates;

            if (!this.masterTemplateId || !masterTemplates[this.masterTemplateId]) {
                return null;
            }

            return masterTemplates[this.masterTemplateId].adType;
        }
    },

    watch: {
        templateFamilyMaster(val: MasterTemplate) {
            if (val?._id && !this.familyMembersToUpdate.includes(val._id)) {
                this.familyMembersToUpdate.push(this.templateFamilyMaster!._id);
            }
        }
    },

    created() {
        /* Trigger the refetching of the upload config in case it has expired. The skip() check
           in the query definition will stop it from being executed if its not needed. */
        this.$apollo.queries.masterTemplateUploadConfig.refetch();

        this.masterTemplateService = new MasterTemplates(this.$apollo, this.clientId);
    },

    methods: {
        async updateTemplate(zipUrl: string) {
            this.familyMembersToUpdate = [this.masterTemplateId];

            try {
                if (!this.masterTemplateService) {
                    throw new Error("MasterTemplateService not initialized.");
                }

                const { data } = await this.masterTemplateService.update(
                    this.masterTemplateId,
                    zipUrl,
                    this.templateType,
                    false
                );

                const { updateMasterTemplate } = data || {};
                const usedInCampaigns = updateMasterTemplate?.usedInCampaigns || [];
                const templateFamily = updateMasterTemplate?.templateFamily || [];

                if (usedInCampaigns.length || templateFamily.length) {
                    const doUpdate = await this.updateTemplatePrompt(data);

                    if (doUpdate) {
                        this.$emit("updateStarted");
                        if (this.familyMembersToUpdate.length > 1) {
                            await this.updateTemplateFamily(zipUrl);
                            this.$emit("familyUpdated", this.familyMembersToUpdate);
                            return;
                        }
                        // hide the modal
                        this.showUpdateTemplatePrompt = false;
                        await this.masterTemplateService.update(this.masterTemplateId, zipUrl, this.templateType, true);
                    } else {
                        bus.$emit("percentLoadEvent", 100);
                        return;
                    }
                }

                this.ga4.trackFeatureEvent({
                    featureEvent: GA4_EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4_FEATURE_CATEGORIES.MASTER_TEMPLATES,
                    featureContext: GA4_FEATURE_CONTEXTS.UPDATE_TEMPLATE,
                    freeformValues: [this.masterTemplateId]
                });

                this.$emit("updated");
                this.$snackbar.success("Template Updated");
            } catch (e) {
                this.$snackbar.error("Upload Failed", (e as Error).message, {
                    duration: 0,
                    closable: true
                });
            }

            bus.$emit("percentLoadEvent", 100);
        },

        async updateTemplateFamily(zipUrl: string) {
            this.familyMembersUpdated = {};
            this.familyUpdateInProgress = true;

            // first we need to update parent to use it as a resize source later
            try {
                if (!this.masterTemplateService) {
                    throw new Error("MasterTemplateService not initialized.");
                }

                await this.masterTemplateService.update(
                    this.templateFamilyMaster!._id,
                    zipUrl,
                    this.templateType,
                    true
                );
                this.familyMembersUpdated = {
                    ...this.familyMembersUpdated,
                    [this.templateFamilyMaster!._id]: true
                };
            } catch (e) {
                this.familyMembersUpdated = {
                    ...this.familyMembersUpdated,
                    [this.templateFamilyMaster!._id]: false
                };

                this.$snackbar.error("Failed to update family's parent template.", (e as Error).message);
                return;
            }

            await Promise.all(
                this.familyMembersToUpdate
                    .filter(id => id !== this.templateFamilyMaster!._id)
                    .map(id => this.updateResize(id, this.templateFamilyMaster!._id))
            );

            this.$emit("familyUpdated", this.familyMembersToUpdate);
            this.$emit("updated");

            this.refetchMasterTemplates?.();

            this.$snackbar.success("Template family Updated");
            this.familyUpdateInProgress = false;
            this.showUpdateTemplatePrompt = false;
        },

        async updateResize(templateId: string, parentId: string) {
            try {
                if (!this.masterTemplateService) {
                    throw new Error("MasterTemplateService not initialized.");
                }

                const result = await this.masterTemplateService.updateResize(templateId, parentId);
                this.familyMembersUpdated = {
                    ...this.familyMembersUpdated,
                    [templateId]: true
                };
                return result;
            } catch (err) {
                this.familyMembersUpdated = {
                    ...this.familyMembersUpdated,
                    [templateId]: false
                };
                console.log(err);
            }
            return Promise.resolve();
        },

        onTemplateUploadError(err: Error) {
            bus.$emit("apolloErrorEvent", err);
        },

        // is called in the MasterTemplateBannerFooterOptions
        // eslint-disable-next-line vue/no-unused-properties
        remoteClick() {
            this.updatePostData();
            (this.$refs.remote as { $el: HTMLElement }).$el.click();
        },

        onTemplateUploadSuccess(_: unknown, file: File) {
            const zipUrl = `${this.uploadConfig?.filePrefix}/${file.name}`;

            this.updateTemplate(zipUrl);
        },

        showUploadPercentage(e: { percent: number }) {
            bus.$emit("percentLoadEvent", e.percent);
        },

        updatePostData() {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4_EVENT_TYPES.FEATURE_ACTIVATE,
                featureCategory: GA4_FEATURE_CATEGORIES.MASTER_TEMPLATES,
                featureContext: GA4_FEATURE_CONTEXTS.ADD_TEMPLATE
            });
            // Required to manually add in the Key for some reason.
            if (!this.uploadConfig || !this.uploadConfig.postData) {
                return;
            }

            const data = JSON.parse(this.uploadConfig.postData);
            data.key = `${this.uploadConfig.filePrefix}/\${filename}`;
            this.formData = data;
        },

        updateTemplatePrompt(data: MasterTemplatesTypes.MasterTemplateUpdateResponse) {
            const {
                updateMasterTemplate: {
                    usedInCampaigns = [],
                    producedAds: rawProducedAds,
                    editableGroupConflicts = { added: [], removed: [] },
                    templateFamily
                } = {}
            } = data;

            const producedAds = rawProducedAds || { published: 0, unpublished: 0 };

            this.showUpdateTemplatePrompt = true;
            this.familyUpdateInProgress = false;

            this.templateUsedInCampaigns = usedInCampaigns;
            this.templateUsedProducedAds = producedAds;
            this.editableGroupConflicts = editableGroupConflicts;
            this.templateFamily = templateFamily || [];

            this.templateFamilyById = this.templateFamily.reduce(
                (acc, curr) => {
                    acc[curr._id] = curr;

                    return acc;
                },
                {} as Record<string, MasterTemplate>
            );

            if (this.templateFamily.length) {
                this.familyMembersToUpdate = this.templateFamily.map(({ _id }) => _id);
            }

            return new Promise(resolve => {
                this.templateUsedPromptResolve = result => {
                    // reset back to base state
                    this.templateUsedPromptResolve = null;
                    this.templateUsedInCampaigns = [];
                    this.templateUsedProducedAds = { published: 0, unpublished: 0 };

                    // resolve the promise with the passed in result
                    resolve(result);
                };
            });
        },

        updateTemplatePromptCancel() {
            this.showUpdateTemplatePrompt = false;
            this.templateUsedPromptResolve?.(false);
        },

        updateTemplatePromptOk() {
            if (this.familyMembersToUpdate.length > 1) {
                this.familyUpdateInProgress = true;
            }

            this.templateUsedPromptResolve?.(true);
        }
    },

    apollo: {
        masterTemplateUploadConfig: {
            query: clientMasterTemplatesUploadConfigQuery,

            variables() {
                return {
                    clientId: this.clientId
                };
            },

            skip() {
                if (!this.canManageMasterTemplates || !this.clientId) {
                    return true;
                }

                if (this.uploadConfigObject?.clientId === this.clientId) {
                    // Check if the one we have is expired or not
                    if (this.uploadConfig && uploadConfigExpired(this.uploadConfig)) {
                        return false;
                    }

                    this.isLoading = false;

                    return true;
                }

                return false;
            },

            manual: true,

            result({ data }) {
                this.$store.dispatch(MasterTemplateLibraryAction.SetUploadConfig, {
                    clientId: this.clientId,
                    config: data.masterTemplateUploadConfig
                });

                this.isLoading = false;
            },

            error(e: Error) {
                bus.$emit("apolloErrorEvent", e);
            }
        }
    }
});
</script>
<style lang="scss" scoped>
@use "@style/variables" as *;
.template-upload-modal {
    p.small-margin {
        margin-bottom: $spacing-small;
    }
    &__button {
        margin-left: $spacing-small;
    }
    p.template-upload-modal__alert-text {
        font-size: $font-size-small;
    }

    &__label {
        font-size: $font-size-small;
    }

    ul {
        margin-bottom: $spacing-small;
    }

    li {
        font-size: $font-size-small;
        margin-left: $spacing-large;
    }

    &__removed {
        margin-top: $spacing-small;
    }
}
</style>
